import React, { useState, useMemo } from "react";

const isTaskDone = (completed) =>
  completed === true || completed === 1 || String(completed) === "true";

export default function PovinnostiMapa({ tasks, formatDateDMY }) {
  const [hideCompleted, setHideCompleted] = useState(false);

  const activeTasks = useMemo(() =>
    (tasks || []).filter(t => !String(t.id).includes("_hist_")),
    [tasks]
  );

  const filteredTasks = useMemo(() => {
    if (hideCompleted) return activeTasks.filter(t => !isTaskDone(t.completed));
    return activeTasks;
  }, [activeTasks, hideCompleted]);

  // Unique companies (sorted)
  const companies = useMemo(() => {
    const set = new Set(filteredTasks.map(t => t.company || "—"));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "sk"));
  }, [filteredTasks]);

  // Unique deadlines (sorted ascending)
  const deadlines = useMemo(() => {
    const set = new Set(filteredTasks.map(t => t.deadline || "").filter(Boolean));
    return Array.from(set).sort();
  }, [filteredTasks]);

  // Grid: { deadline -> { company -> task[] } }
  const grid = useMemo(() => {
    const g = {};
    deadlines.forEach(d => {
      g[d] = {};
      companies.forEach(c => { g[d][c] = []; });
    });
    filteredTasks.forEach(task => {
      const d = task.deadline || "";
      const c = task.company || "—";
      if (g[d]?.[c] !== undefined) g[d][c].push(task);
    });
    return g;
  }, [filteredTasks, companies, deadlines]);

  const colWidth = companies.length > 0 ? Math.floor(85 / companies.length) : 85;

  return (
    <div className="mapa-root">
      <style>{`
        .mapa-root { font-family: Arial, sans-serif; }
        .mapa-controls { display: flex; align-items: center; gap: 16px; margin-bottom: 14px; }
        .mapa-table { border-collapse: collapse; width: 100%; table-layout: fixed; }
        .mapa-table th {
          background: #222; color: #fff; padding: 6px 7px;
          font-size: 11px; text-align: left; border: 1px solid #555;
          word-wrap: break-word;
        }
        .mapa-table td {
          border: 1px solid #ccc; padding: 6px 7px;
          vertical-align: top; word-wrap: break-word; font-size: 11px;
        }
        .mapa-table td.date-col {
          font-weight: bold; font-size: 10px; white-space: nowrap;
          background: #eee; color: #111;
          overflow: hidden; text-overflow: ellipsis;
        }
        .mapa-table th:first-child, .mapa-table td:first-child {
          width: 62px; min-width: 62px; max-width: 62px;
          padding-left: 3px; padding-right: 3px;
        }
        .mapa-table tr:nth-child(even) td:not(.date-col) { background: #f6f6f6; }
        .mapa-table tr:nth-child(odd)  td:not(.date-col) { background: #fff; }
        .task-entry { margin-bottom: 5px; padding-bottom: 4px; border-bottom: 1px dashed #ddd; }
        .task-entry:last-child { margin-bottom: 0; border-bottom: none; }
        .task-name { font-size: 11px; line-height: 1.3; }
        .task-name.done { text-decoration: line-through; color: #999; }
        .task-status { font-size: 9px; color: #555; margin-top: 1px; }
        .task-subtasks { margin: 3px 0 0 0; padding: 0; list-style: none; }
        .task-subtasks li { font-size: 9.5px; padding: 1px 0 1px 10px; position: relative; color: #333; line-height: 1.3; }
        .task-subtasks li::before { content: "·"; position: absolute; left: 2px; }
        .task-subtasks li.st-done { text-decoration: line-through; color: #aaa; }
        .task-note { font-size: 9px; color: #666; font-style: italic; margin-top: 3px; }
        .mapa-empty { padding: 24px; color: #888; text-align: center; }
        @media print {
          .mapa-controls { display: none !important; }
          body * { visibility: hidden; }
          .mapa-root, .mapa-root * { visibility: visible; }
          .mapa-root { position: absolute; top: 0; left: 0; width: 100%; }
          @page { size: A4 landscape; margin: 8mm 7mm; }
          .mapa-table { font-size: 8pt; }
          .mapa-table th { font-size: 8pt; }
          .mapa-table td { font-size: 8pt; padding: 4px 5px; }
          .task-name { font-size: 8pt; }
          .task-subtasks li { font-size: 7.5pt; }
          .task-note { font-size: 7pt; }
        }
      `}</style>

      <div className="mapa-controls">
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={hideCompleted}
            onChange={e => setHideCompleted(e.target.checked)}
          />
          Skryť splnené
        </label>
        <button
          onClick={() => window.print()}
          style={{ padding: "5px 16px", fontSize: 13, cursor: "pointer" }}
        >
          Tlačiť
        </button>
      </div>

      {companies.length === 0 || deadlines.length === 0 ? (
        <div className="mapa-empty">Žiadne povinnosti na zobrazenie.</div>
      ) : (
        <table className="mapa-table">
          <colgroup>
            <col style={{ width: "62px", minWidth: "62px", maxWidth: "62px" }} />
            {companies.map(c => (
              <col key={c} style={{ width: `${colWidth}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th>Termín</th>
              {companies.map(c => <th key={c}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {deadlines.map(d => (
              <tr key={d}>
                <td className="date-col">{formatDateDMY(d)}</td>
                {companies.map(c => (
                  <td key={c}>
                    {(grid[d][c] || []).map(task => (
                      <div key={task.id} className="task-entry">
                        <div className={`task-name${isTaskDone(task.completed) ? " done" : ""}`}>
                          {isTaskDone(task.completed) ? "✓ " : ""}{task.text}
                        </div>
                        {task.subtasks?.length > 0 && (
                          <ul className="task-subtasks">
                            {task.subtasks.map(st => (
                              <li
                                key={st.id}
                                className={isTaskDone(st.completed) ? "st-done" : ""}
                              >
                                {st.text}
                              </li>
                            ))}
                          </ul>
                        )}
                        {task.note && (
                          <div className="task-note">{task.note}</div>
                        )}
                      </div>
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
